package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.layout.MarketingSection
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.layout.PageHero
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.bootstrap.rememberBootstrapStore
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.integrations.PanelAction
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.appcredentials.AnthropicAppCredentialsDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialProviderDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label

@Page("/bootstrap")
@Composable
fun BootstrapPage() {
    MarketingShell {
        val store = rememberBootstrapStore()
        val ctx = rememberPageContext()
        val scope = rememberCoroutineScope()

        LaunchedEffect(Unit) { store.refresh() }

        LaunchedEffect(store.finalized) {
            if (store.finalized) ctx.router.navigateTo("/signin")
        }

        PageHero(
            title = "Let's bootstrap your workspace.",
            subtitle = "Paste your Slack, Linear and Anthropic app credentials. " +
                "Once the first Slack install completes we'll copy them into the workspace " +
                "and retire these endpoints.",
        )

        MarketingSection(maxWidthPx = 760) {
            when (val s = store.loadState) {
                is LoadState.Loading, LoadState.Idle ->
                    SpanText(text = "Loading…", modifier = Modifier.color(Palette.textTertiary))
                is LoadState.Error ->
                    SpanText(
                        text = "Failed to load bootstrap state: ${s.error}",
                        modifier = Modifier.color(Palette.error),
                    )
                LoadState.Ready -> {
                    AnthropicPanel(store, scope)
                    Box(modifier = Modifier.margin(top = Spacing.xl))
                    InstallCta(store)
                }
            }
        }
    }
}

@Composable
private fun AnthropicPanel(
    store: co.maytes.standupbot.dashboard.features.bootstrap.BootstrapStore,
    scope: kotlinx.coroutines.CoroutineScope,
) {
    val preview = store.preview(AppCredentialProviderDto.ANTHROPIC)
    var apiKey by remember { mutableStateOf("") }
    var model by remember { mutableStateOf("claude-sonnet-5-5") }
    var errorMsg by remember { mutableStateOf<String?>(null) }

    ProviderCard(
        title = "Anthropic",
        present = preview?.present == true,
        hint = preview?.model?.let { "model: $it" },
    ) {
        FormField("API Key (starts with sk-ant-)", apiKey, secret = true) { apiKey = it }
        FormField("Model", model) { model = it }
        errorMsg?.let { SpanText(it, Modifier.color(Palette.error).fontSize(Typography.bodySmall.size)) }

        PanelActionsRow(
            isSaving = store.panelAction(AppCredentialProviderDto.ANTHROPIC) == PanelAction.Saving,
            canSave = apiKey.isNotBlank() && model.isNotBlank(),
            onSave = {
                errorMsg = null
                scope.launch {
                    val result =
                        store.saveAnthropic(AnthropicAppCredentialsDto(apiKey = apiKey, model = model))
                    if (result is Result.Err) errorMsg = result.error.toString()
                }
            },
        )
    }
}

@Composable
private fun InstallCta(store: co.maytes.standupbot.dashboard.features.bootstrap.BootstrapStore) {
    val enabled = store.allProvidersConfigured
    if (!enabled) {
        SpanText(
            text = "Configure all three providers above, then click Install on Slack to create " +
                "the first workspace.",
            modifier = Modifier.color(Palette.textSecondary).fontSize(Typography.bodySmall.size),
        )
        return
    }
    Link(
        path = "/oauth/slack/install",
        modifier = Modifier
            .backgroundColor(Palette.primary)
            .color(Palette.onPrimary)
            .padding(topBottom = Spacing.sm, leftRight = Spacing.lg)
            .borderRadius(Radius.md)
            .fontSize(Typography.body.size)
            .fontWeight(600)
            .styleModifier { property("text-decoration", "none") },
    ) {
        SpanText("Install on Slack, create first workspace")
    }
}

@Composable
private fun ProviderCard(
    title: String,
    present: Boolean,
    hint: String?,
    content: @Composable () -> Unit,
) {
    Card(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Column {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .classNames("sb-text-primary")
                        .fontSize(Typography.h3.size)
                        .fontWeight(600),
                )
                if (hint != null) {
                    SpanText(
                        text = hint,
                        modifier = Modifier
                            .color(Palette.textTertiary)
                            .fontSize(Typography.caption.size)
                            .margin(top = Spacing.xxs),
                    )
                }
            }
            StatusChip(present = present)
        }
        Column(modifier = Modifier.fillMaxWidth()) {
            content()
        }
    }
}

@Composable
private fun StatusChip(present: Boolean) {
    val bg = if (present) Palette.success else Palette.surfaceSubtle
    val fg = if (present) Color.rgb(0xFFFFFF) else Palette.textTertiary
    val label = if (present) "Configured" else "Not set"
    Box(
        modifier = Modifier
            .backgroundColor(bg)
            .color(fg)
            .padding(topBottom = 4.px, leftRight = 10.px)
            .borderRadius(Radius.full)
            .fontSize(Typography.caption.size)
            .fontWeight(500),
    ) {
        SpanText(label)
    }
}

@Composable
private fun FormField(
    label: String,
    value: String,
    secret: Boolean = false,
    onChange: (String) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.sm)) {
        Label(attrs = {
            style {
                property("font-size", Typography.caption.size.toString())
                property("font-weight", "500")
                property("color", Palette.textSecondary.cssRgb())
                property("margin-bottom", "4px")
                property("display", "block")
            }
        }) {
            SpanText(label)
        }
        Input(type = if (secret) InputType.Password else InputType.Text) {
            this.value(value)
            this.placeholder(label)
            this.onInput { onChange(it.value) }
            style {
                property("width", "100%")
                property("padding", "10px 12px")
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("border-radius", "${Radius.md.value}px")
                property("font-size", "14px")
                property("font-family", Typography.FONT_SANS)
                property("background", Palette.surface.cssRgb())
                property("color", Palette.textPrimary.cssRgb())
            }
        }
    }
}

@Composable
private fun PanelActionsRow(
    isSaving: Boolean,
    canSave: Boolean,
    onSave: () -> Unit,
) {
    Row(modifier = Modifier.margin(top = Spacing.sm)) {
        org.jetbrains.compose.web.dom.Button(
            attrs = Modifier
                .backgroundColor(if (canSave && !isSaving) Palette.accent else Palette.surfaceSubtle)
                .color(if (canSave && !isSaving) Color.rgb(0xFFFFFF) else Palette.textTertiary)
                .padding(topBottom = Spacing.xs, leftRight = Spacing.md)
                .borderRadius(Radius.md)
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .styleModifier {
                    property("border", "none")
                    property("cursor", if (canSave && !isSaving) "pointer" else "not-allowed")
                }
                .toAttrs {
                    onClick { if (canSave && !isSaving) onSave() }
                },
        ) {
            SpanText(if (isSaving) "Saving…" else "Save")
        }
    }
}

